import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/lib/Seo";
import { PageHead } from "@/pages/About";
import Reveal from "@/components/Reveal";
import { API } from "@/lib/adminApi";

const TYPE_LABEL = {
  article: "مقالة",
  release: "إصدار",
  "case-study": "دراسة حالة",
  research: "بحث",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

export default function Knowledge() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    fetch(`${API}/publications`)
      .then((r) => r.json())
      .then((data) => {
        if (!ignore) setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => !ignore && setItems([]))
      .finally(() => !ignore && setLoading(false));
    return () => { ignore = true; };
  }, []);

  if (!loading && items.length === 0) {
    return (
      <>
        <Seo path="/publications" title="إصداراتنا المعرفية" description="إصداراتنا المعرفية: إصدارات، مقالات، دراسات حالة، وأبحاث متخصصة." />
        <PageHead eyebrow="إصداراتنا المعرفية" title="إصداراتنا المعرفية" subtitle="إصدارات، مقالات، دراسات حالة، وأبحاث متخصصة تسهم في رفع الوعي وتمكين شركائنا من اتخاذ قرارات استراتيجية مستنيرة" />
        <section className="bg-paper py-20">
          <div className="mx-auto max-w-[1400px] px-6 sm:px-10 text-center text-blue/70">
            {loading ? "جارٍ التحميل…" : "لا توجد إصدارات منشورة حاليًا."}
          </div>
        </section>
      </>
    );
  }

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <>
      <Seo path="/publications" title="إصداراتنا المعرفية" description="إصداراتنا المعرفية: إصدارات، مقالات، دراسات حالة، وأبحاث متخصصة." />
      <PageHead eyebrow="إصداراتنا المعرفية" title="إصداراتنا المعرفية" subtitle="إصدارات، مقالات، دراسات حالة، وأبحاث متخصصة تسهم في رفع الوعي وتمكين شركائنا من اتخاذ قرارات استراتيجية مستنيرة" />

      <section className="bg-paper py-20">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
          {featured && (
            <Reveal>
              <Link to={`/knowledge/${featured.slug}`} data-testid={`knowledge-featured-${featured.slug}`} className="group block bg-navy text-white overflow-hidden" style={{ backgroundColor: "var(--navy)" }}>
                {featured.cover_image && (
                  <div className="h-56 sm:h-72 w-full overflow-hidden">
                    <img src={featured.cover_image} alt={featured.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-10 sm:p-14">
                  <span className="text-sm font-semibold" style={{ color: "var(--peach)" }}>{TYPE_LABEL[featured.type] || "إصدار"}</span>
                  <h2 className="mt-4 font-display leading-snug group-hover:text-peach transition-colors" style={{ fontSize: "clamp(1.4rem, 2.6vw, 2.2rem)" }}>{featured.title}</h2>
                  {featured.excerpt && <p className="mt-4 text-white/75 leading-relaxed max-w-2xl">{featured.excerpt}</p>}
                </div>
              </Link>
            </Reveal>
          )}

          <div className="mt-6 border-t border-navy/10" data-testid="knowledge-archive">
            {rest.map((k, i) => (
              <Reveal key={k.slug} delay={i * 0.06}>
                <Link to={`/knowledge/${k.slug}`} data-testid={`knowledge-item-${k.slug}`} className="group flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-7 border-b border-navy/10">
                  <div className="flex items-center gap-5">
                    {k.cover_image && (
                      <img src={k.cover_image} alt="" loading="lazy" decoding="async" className="hidden sm:block w-28 h-20 object-cover shrink-0 bg-navy/5" />
                    )}
                    <div>
                      <span className="text-sm font-semibold" style={{ color: "var(--purple)" }}>{TYPE_LABEL[k.type] || "إصدار"}</span>
                      <h3 className="mt-1 font-display text-navy text-xl group-hover:text-coral transition-colors">{k.title}</h3>
                    </div>
                  </div>
                  <span className="text-sm text-blue">{fmtDate(k.published_at)}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
